import { useState } from "react";
import { Button, DataTable, PageHead, Panel, StatusPill, useToast, type Column } from "@guestpilot/ui-kit";
import { useGet, useHandleApiError, useWrite, type Schema } from "../api";
import { formatDateTime } from "../format";
import { ConfirmDialog } from "../components";

type Attempt = Schema<"EnrollmentAttempt">;

const STATE_LABEL: Record<string, string> = {
  converted: "Converted",
  opted_out: "Opted out",
  expired: "Expired",
  in_follow_up: "In follow-up",
};

function stateOf(a: Attempt, now: number): string {
  if (a.converted) return "converted";
  if (a.optedOut) return "opted_out";
  if (a.expiresAt && new Date(a.expiresAt).getTime() < now) return "expired";
  return "in_follow_up";
}

/** Sign-ups that did not finish consent (BR-265): kept 12 months, then deleted; an opt-out is honoured at once and erases the personal data. */
export function Enrollment() {
  const list = useGet<Attempt[]>("/enrollment-attempts");
  const fail = useHandleApiError();
  const toast = useToast();
  const optOut = useWrite("POST", "/enrollment-attempts/:id/opt-out", [
    "/enrollment-attempts",
    "/customers",
    "/dashboard",
  ]);
  const [target, setTarget] = useState<Attempt | null>(null);
  const now = Date.now();
  const columns: Column<Attempt>[] = [
    { key: "o", header: "Organisation", render: (a) => <span className="t-body-strong">{a.orgName}</span> },
    {
      key: "p",
      header: "Contact",
      render: (a) => (
        <div className="gp-person-text">
          <span>{a.personName}</span>
          <span className="gp-person-sub t-body-sm">{a.personEmail}</span>
        </div>
      ),
    },
    { key: "s", header: "Started", render: (a) => formatDateTime(a.startedAt) },
    { key: "x", header: "Kept until", render: (a) => formatDateTime(a.expiresAt) },
    {
      key: "st",
      header: "State",
      render: (a) => {
        const s = stateOf(a, now);
        return <StatusPill domain="enrollment" value={s} label={STATE_LABEL[s] ?? s} />;
      },
    },
    {
      key: "a",
      header: "Actions",
      render: (a) =>
        !a.converted && !a.optedOut ? (
          <Button variant="ghost" onClick={() => setTarget(a)}>
            Honour opt-out
          </Button>
        ) : null,
    },
  ];
  return (
    <div className="gp-page">
      <PageHead title="Enrollment" subtitle="Sign-ups that did not complete consent." />
      <Panel label="Enrollment attempts">
        <DataTable
          caption="Enrollment attempts"
          columns={columns}
          rows={list.data}
          loading={list.isLoading}
          error={list.isError}
          onRetry={() => void list.refetch()}
          errorTitle="Could not load the attempts."
          retryLabel="Retry"
          rowKey={(a) => a.id ?? ""}
          emptyTitle="No enrollment attempts"
        />
      </Panel>
      <ConfirmDialog
        open={target !== null}
        danger
        title="Honour this opt-out?"
        description={`Follow-up stops and the contact details of ${target?.personName ?? "this person"} are deleted now. This cannot be undone.`}
        confirmLabel="Delete and stop follow-up"
        busy={optOut.isPending}
        onClose={() => setTarget(null)}
        onConfirm={() =>
          target &&
          optOut.mutate(
            { params: { id: target.id ?? "" } },
            {
              onSuccess: () => {
                toast.success("Opt-out honoured");
                setTarget(null);
              },
              onError: fail,
            },
          )
        }
      />
    </div>
  );
}
